import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  DEFAULT_BOOKING_SLOT_INTERVAL_MINUTES,
  filterSlotsAgainstBlocks,
  formatSlotForDisplay,
  formatTimeForDisplay,
  generateServiceSlotsFromAvailability,
  getDayAvailabilityForDate,
  groupBlockedTimesByDate,
  timeStringToMinutes,
  toISODateString,
} from "../../utils/availability";
import { addOnTotals, selectedAddOns } from "../../utils/addOnSelection";
import { previewBookingPayment } from "../../utils/bookingPayment";
import "./inline-slot-picker.css";

// The blocked-times endpoint is the only range endpoint of the three, and the
// booking form already pulls the same 90-day window.
const BLOCKED_TIMES_WINDOW_DAYS = 90;
// The strip is one continuous scroll over the same window the blocked-times
// call covers, as in the design — no pager, because paging was the one control
// the design's panel does not have.
const MAX_DAYS_AHEAD = BLOCKED_TIMES_WINDOW_DAYS;
// How many booked-up days the picker steps past on its own before it leaves
// the choice to the visitor. Each step is one per-date request.
const MAX_AUTO_PICK_STEPS = 14;

export const NO_AVAILABILITY_MESSAGE =
  "No availability on that date. Please choose another.";
const SLOTS_ERROR_MESSAGE =
  "We couldn’t load times for that day. Please choose another.";
const AVAILABILITY_ERROR_MESSAGE =
  "Live availability is unavailable right now. Showing standard openings.";

const dayNameFormatter = new Intl.DateTimeFormat("en-US", { weekday: "short" });
const monthNameFormatter = new Intl.DateTimeFormat("en-US", { month: "short" });
const fullDateFormatter = new Intl.DateTimeFormat("en-US", {
  weekday: "long",
  month: "long",
  day: "numeric",
});

const formatCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

const startOfToday = () => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now;
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const slotKey = (slot) => `${slot.start}-${slot.end}`;

const InlineSlotPicker = ({
  professionalId,
  service,
  availability = [],
  value = null,
  onChange,
  // The illustrative booking page at /example has no professional behind it.
  // In demo mode the picker asks the server nothing and treats the passed
  // availability as the final answer instead of a first guess.
  demoMode = false,
  // Open with the next opening already chosen instead of an empty panel, so a
  // visitor lands on a finished booking rather than something to assemble.
  // Seeded from the locally derived openings, which in demo mode are the whole
  // truth; on a live page the per-date call still has the last word, and the
  // stale-slot effect below drops the seeded time if the two disagree.
  autoSelectFirstOpening = false,
  // Open on the first day with openings and leave the time to the visitor, so
  // the panel starts on times to pick from rather than an empty strip. The
  // per-date call checks the day, and a day that turns out to be booked up is
  // stepped past until the visitor picks a day themselves.
  autoSelectFirstDay = false,
  // Add-ons already ticked in the booking form. Times are then found for the
  // longer appointment. Left out, it's the service's own length, as before.
  addOnIds = [],
  // The service's own payment mode, as BookingForm is given it (see
  // resolveServicePaymentMode). The price summary is checkout's, so it needs it.
  paymentMode = "card",
  // False when the pro has no full address, which books an either-place
  // service at the client's, travel fee and all.
  proLocationBookable = true,
}) => {
  const serviceId = service?._id || "";
  const pickedAddOns = selectedAddOns(service, addOnIds);
  const addOnKey = pickedAddOns.map((addOn) => addOn.id).join(",");
  const { minutes: addOnMinutes, price: addOnPrice } = addOnTotals(pickedAddOns);
  // The provider's configured weekly diary, straight from the profile payload.
  // This is the recurring *pattern*, so it is the only availability source that
  // is meaningful for a date more than a week out. Deliberately NOT the
  // /available-slots endpoint: that one keys its entries by day name and
  // subtracts bookings for only the next occurrence of each weekday, so a
  // booked-out Tuesday would grey out every later Tuesday too.
  const availabilityPattern = useMemo(
    () => (Array.isArray(availability) ? availability : []),
    [availability],
  );
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(true);
  const [availabilityError, setAvailabilityError] = useState("");
  const [daySlots, setDaySlots] = useState([]);
  const [daySlotsLoading, setDaySlotsLoading] = useState(false);
  const [daySlotsError, setDaySlotsError] = useState("");
  // The date the per-date answer above (slots or error) belongs to.
  const [daySlotsDate, setDaySlotsDate] = useState("");
  // The day autoSelectFirstDay chose, and how many booked-up days it has
  // stepped past to get there. Cleared once the visitor picks a day.
  const [autoPick, setAutoPick] = useState({ date: "", steps: 0 });
  // The day the keyboard was last on, so Tab comes back to it.
  const [focusedDay, setFocusedDay] = useState("");
  const dayRequestRef = useRef(0);
  const dayStripRef = useRef(null);
  // Callers pass an inline arrow; keeping it in a ref stops the stale-slot
  // effect below from re-running on every parent render.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  const selectedDate = value?.date || "";
  const selectedTimeSlot = value?.timeSlot || "";
  const today = useMemo(() => startOfToday(), []);
  const todayISO = useMemo(() => toISODateString(today), [today]);
  // Read once, on mount: a default that shifts under the visitor as the clock
  // ticks would be worse than one that is a few minutes stale.
  const minutesIntoToday = useMemo(() => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  }, []);

  // Call 1: the blocked-times window. Combined with the configured pattern above
  // this is enough to decide which days are *plausibly* open; it is not accurate
  // enough to price a specific day, which is what call 2 is for. Blocked times
  // are not service-scoped, so switching service does not re-fetch them.
  useEffect(() => {
    if (demoMode || !professionalId) {
      setAvailabilityLoading(false);
      return undefined;
    }

    let active = true;
    const rangeStart = startOfToday();
    const rangeEnd = addDays(rangeStart, BLOCKED_TIMES_WINDOW_DAYS);

    setAvailabilityLoading(true);
    axios
      .get(`/api/blocked-times/${professionalId}`, {
        // What clients see, even when the viewer could manage this schedule.
        params: {
          start: toISODateString(rangeStart),
          end: toISODateString(rangeEnd),
          view: "busy",
        },
      })
      .then(({ data }) => {
        if (!active) return;
        setBlockedTimes(Array.isArray(data) ? data : []);
        setAvailabilityError("");
      })
      .catch(() => {
        if (!active) return;
        // Without the blocked-times window the pattern is still worth showing;
        // call 2 is what actually decides a chosen day.
        setBlockedTimes([]);
        setAvailabilityError(AVAILABILITY_ERROR_MESSAGE);
      })
      .then(() => {
        if (!active) return;
        setAvailabilityLoading(false);
      });

    return () => {
      active = false;
    };
  }, [demoMode, professionalId]);

  // Call 2: one request for the chosen day only. The configured pattern above
  // knows nothing about bookings, so this is the only authority on whether a
  // specific date actually has openings left.
  useEffect(() => {
    if (demoMode || !selectedDate || !professionalId || !serviceId) {
      setDaySlots([]);
      setDaySlotsError("");
      setDaySlotsDate("");
      setDaySlotsLoading(false);
      return undefined;
    }

    let active = true;
    const requestId = dayRequestRef.current + 1;
    dayRequestRef.current = requestId;
    setDaySlotsLoading(true);

    axios
      .get(`/api/bookings/professional/${professionalId}/available-slots-for-date`, {
        params: {
          serviceId,
          date: selectedDate,
          intervalMinutes: DEFAULT_BOOKING_SLOT_INTERVAL_MINUTES,
          ...(addOnKey ? { addOnIds: addOnKey } : {}),
        },
      })
      .then(({ data }) => {
        if (!active || requestId !== dayRequestRef.current) return;
        const payload = data && typeof data === "object" && !Array.isArray(data) ? data : null;
        const slots = Array.isArray(payload?.slots)
          ? payload.slots
          : Array.isArray(data)
            ? data
            : [];
        setDaySlots(slots);
        setDaySlotsError("");
        setDaySlotsDate(selectedDate);
      })
      .catch((error) => {
        console.error("Error loading slots for date:", error);
        if (!active || requestId !== dayRequestRef.current) return;
        setDaySlots([]);
        setDaySlotsError(SLOTS_ERROR_MESSAGE);
        setDaySlotsDate(selectedDate);
      })
      .then(() => {
        if (!active || requestId !== dayRequestRef.current) return;
        setDaySlotsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [addOnKey, demoMode, professionalId, selectedDate, serviceId]);

  const blockedByDate = useMemo(
    () => groupBlockedTimesByDate(blockedTimes),
    [blockedTimes],
  );

  const plausibleSlotsForDate = useCallback(
    (isoDate) => {
      const dayAvailability = getDayAvailabilityForDate(availabilityPattern, isoDate);
      const durationMinutes = Number(service?.duration) + addOnMinutes;
      const baseSlots =
        Number.isInteger(durationMinutes) && durationMinutes > 0
          ? generateServiceSlotsFromAvailability(dayAvailability?.slots ?? [], {
              serviceDurationMinutes: durationMinutes,
              intervalMinutes: DEFAULT_BOOKING_SLOT_INTERVAL_MINUTES,
            })
          : (dayAvailability?.slots ?? []);
      if (!baseSlots.length) return [];
      return filterSlotsAgainstBlocks(baseSlots, blockedByDate.get(isoDate) ?? []);
    },
    [addOnMinutes, availabilityPattern, blockedByDate, service?.duration],
  );

  const days = useMemo(() => {
    let previousMonth = -1;
    return Array.from({ length: MAX_DAYS_AHEAD }, (_, index) => {
      const date = addDays(today, index);
      const iso = toISODateString(date);
      const month = date.getMonth();
      // Scrolling crosses month boundaries, so the cell that opens a month
      // names it instead of repeating a weekday. Every cell keeps its size,
      // and each button's own label carries the full date either way.
      const startsMonth = month !== previousMonth;
      previousMonth = month;
      return {
        iso,
        date,
        label: startsMonth
          ? monthNameFormatter.format(date)
          : dayNameFormatter.format(date),
        // "Plausibly open" only. The per-date call is what decides for real.
        open: plausibleSlotsForDate(iso).length > 0,
      };
    });
  }, [plausibleSlotsForDate, today]);

  const openDays = useMemo(() => days.filter((day) => day.open), [days]);
  const hasOpenDay = openDays.length > 0;
  // The strip is one Tab stop, not one per day: the day last focused, else the
  // chosen day, else the first open one. Arrow keys move between open days.
  const tabStopDay =
    [focusedDay, selectedDate].find(
      (iso) => iso && openDays.some((day) => day.iso === iso),
    ) ||
    openDays[0]?.iso ||
    "";

  const openSlots = useMemo(() => {
    if (!selectedDate) return [];
    // No call 2 in demo mode, so the locally derived openings *are* the answer.
    if (demoMode) return plausibleSlotsForDate(selectedDate);
    if (daySlotsError) return [];
    return filterSlotsAgainstBlocks(daySlots, blockedByDate.get(selectedDate) ?? []);
  }, [
    blockedByDate,
    daySlots,
    daySlotsError,
    demoMode,
    plausibleSlotsForDate,
    selectedDate,
  ]);

  // A slot can disappear between the day being drawn as open and the per-date
  // answer arriving. Drop a stale selection rather than carry it into the modal.
  useEffect(() => {
    if (!selectedTimeSlot || daySlotsLoading || !selectedDate) return;
    if (openSlots.some((slot) => slotKey(slot) === selectedTimeSlot)) return;
    onChangeRef.current?.({ date: selectedDate, timeSlot: "" });
  }, [daySlotsLoading, openSlots, selectedDate, selectedTimeSlot]);

  // The first opening a visitor could actually take. The day strip draws today
  // from the weekly pattern alone, so today's morning slots are still "open"
  // at six in the evening; a default lands on one of those only over this
  // filter's dead body, and moves to the next open day instead.
  const autoSelects = autoSelectFirstOpening || autoSelectFirstDay;
  const firstOpening = useMemo(() => {
    if (!autoSelects) return null;
    for (const day of days) {
      if (!day.open) continue;
      const slots = plausibleSlotsForDate(day.iso).filter(
        (slot) =>
          day.iso !== todayISO || timeStringToMinutes(slot.start) > minutesIntoToday,
      );
      if (slots.length) return { date: day.iso, timeSlot: slotKey(slots[0]) };
    }
    return null;
  }, [autoSelects, days, minutesIntoToday, plausibleSlotsForDate, todayISO]);

  // Only ever into an empty panel. Nothing in this component clears the chosen
  // *date* — clearing a time keeps its day — so this seeds on mount and again
  // when the caller hands over a fresh panel, which is what switching service
  // does, and never argues with a visitor who is clicking around.
  useEffect(() => {
    if (!autoSelects || availabilityLoading) return;
    if (selectedDate || !firstOpening) return;
    if (autoSelectFirstOpening) {
      onChangeRef.current?.(firstOpening);
      return;
    }
    setAutoPick({ date: firstOpening.date, steps: 0 });
    onChangeRef.current?.({ date: firstOpening.date, timeSlot: "" });
  }, [autoSelectFirstOpening, autoSelects, availabilityLoading, firstOpening, selectedDate]);

  // The strip only knows the weekly pattern, so the day chosen for the visitor
  // can come back booked up. While its answer is on the way, and while the
  // picker moves on from a booked-up day to the next open one, the panel says
  // it is still checking rather than "no availability".
  const onAutoPickedDay =
    Boolean(autoPick.date) &&
    autoPick.date === selectedDate &&
    !demoMode &&
    Boolean(professionalId && serviceId);
  const autoPickAnswered =
    onAutoPickedDay && !daySlotsLoading && daySlotsDate === selectedDate;
  const nextDayToTry =
    autoPickAnswered &&
    // A failed lookup stops the walk, so an outage costs a request or two
    // rather than one per day. Today is the exception: the server turns away
    // any date before its own UTC today, which west of UTC is the visitor's
    // today every evening, so a failed today steps on to the next open day.
    (!daySlotsError || selectedDate === todayISO) &&
    openSlots.length === 0 &&
    autoPick.steps < MAX_AUTO_PICK_STEPS
      ? days.find((day) => day.open && day.iso > selectedDate)?.iso || ""
      : "";
  const checkingAutoPick = (onAutoPickedDay && !autoPickAnswered) || Boolean(nextDayToTry);

  useEffect(() => {
    if (!nextDayToTry) return;
    setAutoPick((current) => ({ date: nextDayToTry, steps: current.steps + 1 }));
    onChangeRef.current?.({ date: nextDayToTry, timeSlot: "" });
  }, [nextDayToTry]);

  // A day chosen for the visitor can sit past the visible end of the strip,
  // which on a phone is only a few days long. Scroll the strip to it; the page
  // itself never moves.
  useEffect(() => {
    if (!autoPick.date || autoPick.date !== selectedDate) return;
    const strip = dayStripRef.current;
    const day = strip?.querySelector('[aria-pressed="true"]');
    if (!day || typeof strip.scrollTo !== "function") return;
    const stripBox = strip.getBoundingClientRect();
    const dayBox = day.getBoundingClientRect();
    if (dayBox.left >= stripBox.left && dayBox.right <= stripBox.right) return;
    strip.scrollTo({ left: strip.scrollLeft + dayBox.left - stripBox.left });
  }, [autoPick.date, selectedDate]);

  const handleDaySelect = (iso) => {
    // Safari does not focus a button on click, so record the day here too.
    setFocusedDay(iso);
    if (iso === selectedDate) return;
    // The visitor's own choice: it stays put, booked up or not.
    setAutoPick((current) => (current.date ? { date: "", steps: 0 } : current));
    onChange?.({ date: iso, timeSlot: "" });
  };

  const handleDayKeyDown = (event, iso) => {
    const index = openDays.findIndex((day) => day.iso === iso);
    let target;
    if (event.key === "ArrowRight") target = openDays[index + 1];
    else if (event.key === "ArrowLeft") target = openDays[index - 1];
    else if (event.key === "Home") target = openDays[0];
    else if (event.key === "End") target = openDays[openDays.length - 1];
    else return;
    event.preventDefault();
    if (!target) return;
    setFocusedDay(target.iso);
    // Focusing it also scrolls the strip to it.
    dayStripRef.current?.querySelector(`[data-date="${target.iso}"]`)?.focus();
  };

  const handleSlotSelect = (slot) => {
    const key = slotKey(slot);
    onChange?.({ date: selectedDate, timeSlot: key === selectedTimeSlot ? "" : key });
  };

  // What checkout opens on for this booking, by the same rules BookingForm
  // charges with, so the panel never promises a different split.
  const priceSummary = useMemo(
    () => previewBookingPayment(service || {}, { paymentMode, proLocationBookable, addOnPrice }),
    [addOnPrice, paymentMode, proLocationBookable, service],
  );
  const showSummary = Boolean(selectedDate && selectedTimeSlot);

  return (
    <div className="inline-slot-picker">
      <div className="inline-slot-picker__section">
        {availabilityLoading ? (
          <p className="inline-slot-picker__note">Loading availability…</p>
        ) : (
          <>
            <div
              className="inline-slot-picker__days"
              role="group"
              aria-label="Pick a day"
              ref={dayStripRef}
            >
              {days.map((day) => (
                <button
                  key={day.iso}
                  type="button"
                  className={`inline-slot-picker__day${
                    day.iso === selectedDate ? " is-selected" : ""
                  }`}
                  data-date={day.iso}
                  tabIndex={day.iso === tabStopDay ? 0 : -1}
                  onClick={() => handleDaySelect(day.iso)}
                  onFocus={() => setFocusedDay(day.iso)}
                  onKeyDown={(event) => handleDayKeyDown(event, day.iso)}
                  disabled={!day.open}
                  aria-pressed={day.iso === selectedDate}
                  aria-label={`${fullDateFormatter.format(day.date)}${
                    day.open ? "" : " — unavailable"
                  }`}
                >
                  <span className="inline-slot-picker__day-name">{day.label}</span>
                  <span className="inline-slot-picker__day-number">{day.date.getDate()}</span>
                </button>
              ))}
            </div>
            {!hasOpenDay && (
              <p className="inline-slot-picker__note inline-slot-picker__note--warning">
                No openings in the next {MAX_DAYS_AHEAD} days.
              </p>
            )}
            {availabilityError && (
              <p className="inline-slot-picker__note inline-slot-picker__note--warning">
                {availabilityError}
              </p>
            )}
          </>
        )}
      </div>

      {selectedDate && (
        <div className="inline-slot-picker__section">
          {daySlotsLoading || checkingAutoPick ? (
            <p className="inline-slot-picker__note">Checking times…</p>
          ) : daySlotsError ? (
            <p className="inline-slot-picker__note inline-slot-picker__note--warning">
              {daySlotsError}
            </p>
          ) : openSlots.length === 0 ? (
            <p className="inline-slot-picker__note inline-slot-picker__note--warning">
              {NO_AVAILABILITY_MESSAGE}
            </p>
          ) : (
            <div className="inline-slot-picker__times" role="group" aria-label="Pick a time">
              {openSlots.map((slot) => {
                const key = slotKey(slot);
                return (
                  <button
                    key={key}
                    type="button"
                    className={`inline-slot-picker__time${
                      key === selectedTimeSlot ? " is-selected" : ""
                    }`}
                    onClick={() => handleSlotSelect(slot)}
                    aria-pressed={key === selectedTimeSlot}
                    // The chip shows a start time, as the design does — the
                    // length is already stated above the strip. The full range
                    // stays the accessible name so nothing is lost to a
                    // screen reader.
                    aria-label={formatSlotForDisplay(slot)}
                  >
                    {formatTimeForDisplay(slot.start) || formatSlotForDisplay(slot)}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {showSummary && (
        <dl className="inline-slot-picker__summary">
          {priceSummary.amountDueToday > 0 && priceSummary.amountDueAtAppointment > 0 ? (
            <>
              {/* The full price only earns a line once it is being split; paid
                  in one go, the single row below already is the full price.
                  Checkout only splits it for a deposit taken online. */}
              <div className="inline-slot-picker__summary-row">
                <dt>Service</dt>
                <dd>{formatCurrency(priceSummary.total)}</dd>
              </div>
              <div className="inline-slot-picker__summary-row inline-slot-picker__summary-row--due">
                <dt>Deposit today</dt>
                <dd>{formatCurrency(priceSummary.amountDueToday)}</dd>
              </div>
              <div className="inline-slot-picker__summary-row inline-slot-picker__summary-row--rest">
                <dt>Rest on the day</dt>
                <dd>{formatCurrency(priceSummary.amountDueAtAppointment)}</dd>
              </div>
            </>
          ) : priceSummary.amountDueToday > 0 ? (
            <div className="inline-slot-picker__summary-row inline-slot-picker__summary-row--due">
              <dt>Due today</dt>
              <dd>{formatCurrency(priceSummary.amountDueToday)}</dd>
            </div>
          ) : priceSummary.amountDueAtAppointment > 0 ? (
            <div className="inline-slot-picker__summary-row">
              <dt>Due on the day</dt>
              <dd>{formatCurrency(priceSummary.amountDueAtAppointment)}</dd>
            </div>
          ) : (
            <div className="inline-slot-picker__summary-row">
              <dt>No payment due</dt>
              <dd>{formatCurrency(0)}</dd>
            </div>
          )}
          {priceSummary.appliedTravelFee > 0 && (
            <p className="inline-slot-picker__note">
              Includes a {formatCurrency(priceSummary.appliedTravelFee)} travel fee.
            </p>
          )}
        </dl>
      )}
    </div>
  );
};

export default InlineSlotPicker;
